import { usePublication } from "@/OneLiners";
import {
    ActionResponse,
    ActionStatusHelper,
    DOMAIN_CONFIGS,
    DomainFormHook,
    DomainHookProps,
    useAppEnum,
    useFormLifeCycle,
} from "@/Shared";
import { usePublicationEnricher } from "./usePublicationEnricher";
import { PublicationFormViewModel } from "../ViewModels";
import { PublicationValidationSchema } from "../PublicationFormValidation";
import { PublicationModel } from "../Models";

/**
 * usePublicationForm hook – manages contact form data and interactions.
 * 
 * Author: Samano CASTRE  
 * Date: July 17, 2025
 * 
 * Responsibilities:
 * - Initialises contact form data by fetching current contact and agent list
 * - Tracks form submission state and response (create or update)
 * - Exposes form logic to the component layer
 * 
 * Behavior:
 * - On mount, fetches the current contact (if editing) and the list of agents
 * - Automatically switches between `createPublication` or `updatePublication` based on contact presence
 * - Tracks the response of the latest action to provide submission feedback
 */
export const usePublicationForm = (props: DomainHookProps): DomainFormHook => {
    const { extraArgs, permissionHook } = props;
    const { property } = extraArgs ?? {};
    const { domainType } = DOMAIN_CONFIGS.Publication;

    /**
     * Contact domain operations and related backend state.
     * Exposes CRUD actions, current entity fetching and associated responses.
     */
    const publicationHook = usePublication();

    /**
     * Contact enrichment layer.
     * Provides additional data (users, computed fields, etc.)
     * required to build the form view model.
     */
    const {
        userMap,
        propertyMap,
        enricherResponse,
        getEnrichedPublication,

    } = usePublicationEnricher();

    /**
         * Contact-related enums exposed to the UI layer.
         * Used to build the form view model.
         */
    const { PublicationEnums } = useAppEnum();

    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            publicationHook.stateResponse.status,
            enricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error: publicationHook.stateResponse.error ??
            enricherResponse.error
    };

    const lifeCycleHook = useFormLifeCycle({
        response,
        domainType,
        props,
        entityHook: publicationHook,
        buildVM: (currentEntity: PublicationModel) => {
            return new PublicationFormViewModel({
                viewEnums: PublicationEnums,
                publication: getEnrichedPublication(currentEntity),
                userMap,
                propertyMap,
                property
            })
        },
    });

    /**
     * Public API exposed to the component layer.
     * Only data and actions strictly required by the UI are returned.
     */
    return {
        // Form
        ...lifeCycleHook,
        viewEnums: PublicationEnums,
        ValidationSchema: PublicationValidationSchema(PublicationEnums),
    }
}